/*
 * Swush search overlay, cart drawer and "added to cart" toast.
 * Both overlays are modal <dialog>s, so focus trapping, Esc and an inert page come for free.
 * Their content is Liquid, fetched with the Section Rendering API:
 *   search  -> /search?q=…&section_id=swush-search-results
 *   cart    -> bundled `sections` on /cart/change.js and /cart/add.js, or /?sections=…
 *   recs    -> /recommendations/products?…&section_id=swush-cart-recs
 */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const root = window.Shopify?.routes?.root ?? '/';
const DURATION = 320;

let scrollLocks = 0;
function lockScroll(on) {
  scrollLocks = Math.max(0, scrollLocks + (on ? 1 : -1));
  document.documentElement.classList.toggle('sw-scroll-lock', scrollLocks > 0);
}

function parseHTML(html, selector) {
  return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
}

function renderCartCount(count) {
  document.querySelectorAll('[data-sw-cart-count]').forEach((badge) => {
    badge.textContent = String(count);
    badge.hidden = count === 0;
    if (reducedMotion.matches) return;
    badge.classList.remove('is-bumped');
    void badge.offsetWidth;
    badge.classList.add('is-bumped');
  });
}

/* Open/close with a CSS transition: `is-open` on the dialog drives the animation. */
class SwOverlay extends HTMLElement {
  setupDialog(dialog) {
    this.dialog = dialog;
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.closeDialog();
    });
  }

  disconnectedCallback() {
    clearTimeout(this.closeTimer);
    if (this.dialog?.open) lockScroll(false);
  }

  openDialog() {
    if (!this.dialog) return;
    if (this.closing) {
      clearTimeout(this.closeTimer);
      this.closing = false;
      this.dialog.classList.add('is-open');
      return;
    }
    if (this.dialog.open) return;
    this.dialog.showModal();
    lockScroll(true);
    requestAnimationFrame(() => this.dialog.classList.add('is-open'));
  }

  closeDialog = () => {
    if (!this.dialog?.open || this.closing) return;
    this.closing = true;
    this.dialog.classList.remove('is-open');
    this.closeTimer = setTimeout(() => {
      this.closing = false;
      this.dialog.close();
      lockScroll(false);
    }, reducedMotion.matches ? 0 : DURATION);
  };
}

/* ------------------------------------------------------------------ */
/* Search                                                              */
/* ------------------------------------------------------------------ */

class SwSearch extends SwOverlay {
  connectedCallback() {
    this.setupDialog(this.querySelector('dialog'));
    this.input = this.querySelector('[data-sw-search-input]');
    this.clearButton = this.querySelector('[data-sw-search-clear]');
    this.popular = this.querySelector('[data-sw-search-popular]');
    this.status = this.querySelector('[data-sw-search-status]');
    this.grid = this.querySelector('[data-sw-search-grid]');
    this.empty = this.querySelector('[data-sw-search-empty]');
    this.emptyTitle = this.querySelector('[data-sw-search-empty-title]');
    this.all = this.querySelector('[data-sw-search-all]');

    this.addEventListener('click', this.onClick);
    this.input.addEventListener('input', this.onInput);
    document.addEventListener('click', this.onOpenerClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.onOpenerClick);
    clearTimeout(this.timer);
    this.controller?.abort();
  }

  onOpenerClick = (event) => {
    if (!event.target.closest('[data-sw-search-open]')) return;
    event.preventDefault();
    this.openDialog();
    this.input.focus({ preventScroll: true });
  };

  onClick = (event) => {
    if (event.target.closest('[data-sw-search-close]')) {
      this.closeDialog();
      return;
    }
    if (event.target.closest('[data-sw-search-clear]')) {
      this.input.value = '';
      this.onInput();
      this.input.focus({ preventScroll: true });
      return;
    }
    const term = event.target.closest('[data-sw-search-term]');
    if (term) {
      this.input.value = term.dataset.swSearchTerm;
      this.onInput();
      this.input.focus({ preventScroll: true });
    }
  };

  onInput = () => {
    const query = this.input.value.trim();
    this.clearButton.hidden = this.input.value === '';
    clearTimeout(this.timer);
    this.controller?.abort();
    if (!query) {
      this.show('popular');
      return;
    }
    this.grid.setAttribute('aria-busy', 'true');
    this.timer = setTimeout(() => this.search(query), 200);
  };

  search(query) {
    const controller = (this.controller = new AbortController());
    const params = new URLSearchParams({ q: query, type: 'product', 'options[prefix]': 'last', section_id: this.dataset.section });
    fetch(`${this.dataset.searchUrl}?${params}`, { signal: controller.signal })
      .then((response) => response.text())
      .then((html) => {
        const results = parseHTML(html, '[data-sw-search-results]');
        this.render(query, Number(results?.dataset.count || 0), results?.innerHTML.trim() || '');
      })
      .catch((error) => {
        if (error.name !== 'AbortError') console.error(error);
      })
      .finally(() => {
        if (this.controller === controller) this.grid.removeAttribute('aria-busy');
      });
  }

  render(query, count, html) {
    if (!count || !html) {
      this.emptyTitle.textContent = this.dataset.textEmpty.replace('[q]', query);
      this.show('empty');
      return;
    }
    const word = count % 10 === 1 && count % 100 !== 11 ? this.dataset.textOne : this.dataset.textMany;
    this.status.textContent = `${count} ${word} ${this.dataset.textFor.replace('[q]', query)}`;
    this.grid.innerHTML = html;
    const params = new URLSearchParams({ q: query, type: 'product', 'options[prefix]': 'last' });
    this.all.href = `${this.dataset.searchUrl}?${params}`;
    this.all.textContent = this.dataset.textAll.replace('[broj]', count);
    this.show('results');
  }

  show(state) {
    this.popular.hidden = state !== 'popular';
    this.status.hidden = state !== 'results';
    this.grid.hidden = state !== 'results';
    this.all.hidden = state !== 'results';
    this.empty.hidden = state !== 'empty';
  }
}

/* ------------------------------------------------------------------ */
/* Cart drawer + toast                                                 */
/* ------------------------------------------------------------------ */

class SwCartDrawer extends SwOverlay {
  connectedCallback() {
    this.setupDialog(this.querySelector('[data-sw-cart-dialog]'));
    this.toast = this.querySelector('[data-sw-toast]');
    this.sectionId = this.dataset.sectionId;

    this.addEventListener('click', this.onClick);
    document.addEventListener('click', this.onOpenerClick);
    document.addEventListener('shopify:cart:lines-update', this.onLinesUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.onOpenerClick);
    document.removeEventListener('shopify:cart:lines-update', this.onLinesUpdate);
    clearTimeout(this.toastTimer);
  }

  get panel() {
    return this.querySelector('[data-sw-cart-panel]');
  }

  open() {
    this.hideToast();
    this.openDialog();
    this.loadRecs();
  }

  onOpenerClick = (event) => {
    if (!event.target.closest('[data-sw-cart-open]')) return;
    event.preventDefault();
    this.open();
  };

  onClick = (event) => {
    if (event.target.closest('[data-sw-cart-close]')) {
      this.closeDialog();
      return;
    }
    if (event.target.closest('[data-sw-toast-open]')) {
      this.open();
      return;
    }
    const qty = event.target.closest('[data-sw-qty]');
    if (qty && !qty.disabled) {
      this.changeQuantity(qty);
      return;
    }
    const add = event.target.closest('[data-sw-rec-add]');
    if (add && !add.disabled) {
      add.disabled = true;
      this.request('cart/add.js', { items: [{ id: Number(add.dataset.swRecAdd), quantity: 1 }] });
    }
  };

  changeQuantity(button) {
    const quantity = Number(button.dataset.swQty);
    const item = button.closest('.sw-cart__item');
    if (item) {
      item.classList.add(quantity === 0 ? 'is-removing' : 'is-busy');
      item.querySelectorAll('button').forEach((control) => (control.disabled = true));
      const value = item.querySelector('[data-sw-qty-value]');
      if (value && quantity > 0) value.textContent = String(quantity);
    }
    this.request('cart/change.js', { id: button.dataset.key, quantity });
  }

  request(path, body) {
    return fetch(`${root}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: this.sectionId, sections_url: window.location.pathname }),
    })
      .then((response) => response.json())
      .then((data) => {
        const html = data.sections?.[this.sectionId];
        if (html) this.swap(html);
        else return this.refresh();
      })
      .catch((error) => {
        console.error(error);
        return this.refresh();
      });
  }

  refresh() {
    return fetch(`${root}?sections=${this.sectionId}`)
      .then((response) => response.json())
      .then((data) => {
        if (data[this.sectionId]) this.swap(data[this.sectionId]);
      })
      .catch((error) => console.error(error));
  }

  swap(html) {
    const fresh = parseHTML(html, '[data-sw-cart-panel]');
    const panel = this.panel;
    if (!fresh || !panel) return;

    const oldBar = panel.querySelector('[data-sw-cart-bar]');
    const oldPct = oldBar?.style.getPropertyValue('--sw-pct');
    const oldRecs = panel.querySelector('[data-sw-cart-recs]');
    const oldRecsWrap = panel.querySelector('[data-sw-cart-recs-wrap]');
    const scrollTop = panel.querySelector('[data-sw-cart-scroll]')?.scrollTop ?? 0;

    panel.innerHTML = fresh.innerHTML;
    panel.dataset.count = fresh.dataset.count;

    const scroll = panel.querySelector('[data-sw-cart-scroll]');
    if (scroll) scroll.scrollTop = scrollTop;

    // Let the shipping bar slide from its old value instead of jumping.
    const bar = panel.querySelector('[data-sw-cart-bar]');
    if (bar && oldPct && !reducedMotion.matches) {
      const pct = bar.style.getPropertyValue('--sw-pct');
      bar.style.setProperty('--sw-pct', oldPct);
      requestAnimationFrame(() => requestAnimationFrame(() => bar.style.setProperty('--sw-pct', pct)));
    }

    // Keep the previous recommendations on screen while the new ones load.
    const recs = panel.querySelector('[data-sw-cart-recs]');
    const recsWrap = panel.querySelector('[data-sw-cart-recs-wrap]');
    if (recs && oldRecs && oldRecsWrap && !oldRecsWrap.hidden) {
      recs.innerHTML = oldRecs.innerHTML;
      recsWrap.hidden = false;
    }

    renderCartCount(Number(fresh.dataset.count || 0));
    if (this.dialog.open) this.loadRecs();
  }

  loadRecs() {
    const panel = this.panel;
    const recs = panel?.querySelector('[data-sw-cart-recs]');
    const wrap = panel?.querySelector('[data-sw-cart-recs-wrap]');
    const productId = recs?.dataset.productId;
    if (!recs || !wrap || !productId) return;

    const token = (this.recsToken = {});
    const load = (intent) => {
      const params = new URLSearchParams({ product_id: productId, limit: '10', intent, section_id: this.dataset.recsSection });
      return fetch(`${root}recommendations/products?${params}`)
        .then((response) => response.text())
        .then((html) => parseHTML(html, '[data-sw-recs]')?.innerHTML.trim() || '');
    };

    load('complementary')
      .then((html) => html || load('related'))
      .then((html) => {
        if (token !== this.recsToken || !recs.isConnected) return;
        recs.innerHTML = html;
        wrap.hidden = !html;
      })
      .catch(() => {});
  }

  /* Product page add to cart: refresh the drawer and show the toast (the drawer stays closed). */
  onLinesUpdate = (event) => {
    if (this.contains(event.target)) return;
    const source = event.target;
    event.promise
      ?.then(({ detail }) => {
        this.refresh();
        if (!detail || detail.didError || detail.source !== 'sw-product') return;
        const variantId = Number(source?.querySelector?.('[data-sw-variant-id]')?.value);
        const item = detail.items?.find((line) => line.variant_id === variantId) || detail.items?.[0];
        if (item) this.showToast(item);
      })
      .catch(() => {});
  };

  showToast(item) {
    if (!this.toast || this.dialog.open) return;
    const image = this.toast.querySelector('[data-sw-toast-img]');
    const sub = this.toast.querySelector('[data-sw-toast-sub]');
    const src = item.image || item.featured_image?.url;
    if (src) image.src = `${src}${src.includes('?') ? '&' : '?'}width=96`;
    image.hidden = !src;
    const variant = item.variant_title && !/default title/i.test(item.variant_title) ? ` · ${item.variant_title}` : '';
    sub.textContent = `${item.product_title}${variant}`;

    this.toast.hidden = false;
    this.toast.classList.remove('is-in');
    void this.toast.offsetWidth;
    this.toast.classList.add('is-in');
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(this.hideToast, 3500);
  }

  hideToast = () => {
    clearTimeout(this.toastTimer);
    if (this.toast) this.toast.hidden = true;
  };
}

if (!customElements.get('sw-search')) customElements.define('sw-search', SwSearch);
if (!customElements.get('sw-cart-drawer')) customElements.define('sw-cart-drawer', SwCartDrawer);
